import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardMedia,
  IconButton,
  Chip,
  Stack,
  useTheme,
  alpha,
  Fade,
  Grow,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  Image as ImageIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  PhotoLibrary as GalleryIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

// FIXED: API URL with fallback to production API
const API_URL = process.env.REACT_APP_API_URL || 'https://api.inkedupmanager.com';

// Budget range options
const BUDGET_RANGES = [
  { value: 'under_200', label: 'Under $200' },
  { value: '200_500', label: '$200 - $500' },
  { value: '500_1000', label: '$500 - $1,000' },
  { value: '1000_2500', label: '$1,000 - $2,500' },
  { value: '2500_plus', label: '$2,500+' },
  { value: 'discuss', label: 'Prefer to discuss' }
];

// Timeframe options
const TIMEFRAME_OPTIONS = [
  { value: 'asap', label: 'As soon as possible' },
  { value: '1_2_weeks', label: '1-2 weeks' },
  { value: '3_4_weeks', label: '3-4 weeks' },
  { value: '1_2_months', label: '1-2 months' },
  { value: 'flexible', label: 'Flexible timing' },
  { value: 'discuss', label: 'Prefer to discuss' }
];

// Referral source options - How did you hear about me
const REFERRAL_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google Search' },
  { value: 'friend_family', label: 'Friend or Family' },
  { value: 'previous_client', label: 'Previous Client' },
  { value: 'walk_in', label: 'Walked by the shop' },
  { value: 'convention', label: 'Tattoo Convention' },
  { value: 'other', label: 'Other' }
];

// Image upload component
const ImageUploader = ({ images, onImagesChange, maxImages = 3 }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const remainingSlots = maxImages - images.length;
    const filesToAdd = files.slice(0, remainingSlots);

    const newImages = filesToAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }));

    onImagesChange([...images, ...newImages]);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    onImagesChange(updatedImages);
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GalleryIcon fontSize="small" />
        Reference Images (Optional - Max {maxImages})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload photos of tattoo ideas, style references, or placement photos
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        id="image-upload-input"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <Grid container spacing={1}>
        {images.map((img) => (
          <Grid item xs={4} key={img.id}>
            <Grow in={true}>
              <Card 
                sx={{ 
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <CardMedia
                  component="img"
                  height="120"
                  image={img.preview}
                  alt="Reference"
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.main'
                    }
                  }}
                  onClick={() => handleRemoveImage(img.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Card>
            </Grow>
          </Grid>
        ))}

        {/* Add image button */}
        {images.length < maxImages && (
          <Grid item xs={4}>
            <label htmlFor="image-upload-input">
              <Paper
                sx={{
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                <UploadIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Add Photo
                </Typography>
              </Paper>
            </label>
          </Grid>
        )}
      </Grid>

      {images.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {images.length} of {maxImages} images uploaded
        </Typography>
      )}
    </Box>
  );
};

// Custom Form Field Renderer - for when using custom form templates
const CustomFieldRenderer = ({ field, value, onChange }) => {
  // Handle checkbox group - when options array exists, it's a multi-select
  const handleCheckboxChange = (option, checked) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (checked) {
      onChange(field.id, [...currentValues, option]);
    } else {
      onChange(field.id, currentValues.filter(v => v !== option));
    }
  };

  switch (field.type) {
    case 'text_input':
    case 'text':
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          required={field.required}
          placeholder={field.placeholder}
          helperText={field.helpText}
          multiline={field.multiline}
          rows={field.multiline ? 4 : 1}
        />
      );
    
    case 'textarea':
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          required={field.required}
          placeholder={field.placeholder}
          helperText={field.helpText}
          multiline
          rows={4}
        />
      );
    
    case 'checkbox':
      // If field has options, render as a group of checkboxes (multi-select)
      if (field.options && field.options.length > 0) {
        const currentValues = Array.isArray(value) ? value : [];
        return (
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              {field.label}{field.required && ' *'}
            </FormLabel>
            {field.helpText && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {field.helpText}
              </Typography>
            )}
            <FormGroup>
              {field.options.map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={currentValues.includes(option)}
                      onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </FormControl>
        );
      }
      // Single checkbox (boolean)
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={value || false}
              onChange={(e) => onChange(field.id, e.target.checked)}
            />
          }
          label={field.label}
        />
      );
    
    case 'yes_no':
      return (
        <FormControl fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
          {field.helpText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {field.helpText}
            </Typography>
          )}
        </FormControl>
      );
    
    case 'radio':
    case 'radio_group':
      return (
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            {field.label}{field.required && ' *'}
          </FormLabel>
          {field.helpText && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              {field.helpText}
            </Typography>
          )}
          <RadioGroup
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
          >
            {(field.options || []).map((option, idx) => (
              <FormControlLabel
                key={idx}
                value={typeof option === 'string' ? option : option.value}
                control={<Radio />}
                label={typeof option === 'string' ? option : option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    
    case 'dropdown':
      return (
        <FormControl fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            label={field.label}
            required={field.required}
          >
            {(field.options || []).map((option, idx) => (
              <MenuItem key={idx} value={typeof option === 'string' ? option : option.value}>
                {typeof option === 'string' ? option : option.label}
              </MenuItem>
            ))}
          </Select>
          {field.helpText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {field.helpText}
            </Typography>
          )}
        </FormControl>
      );
    
    case 'date':
      return (
        <TextField
          fullWidth
          type="date"
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          required={field.required}
          helperText={field.helpText}
          InputLabelProps={{ shrink: true }}
        />
      );
    
    case 'section':
      // Section headers - just display as a divider with title
      return (
        <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {field.label}
          </Typography>
          {field.helpText && (
            <Typography variant="body2" color="text.secondary">
              {field.helpText}
            </Typography>
          )}
          <Divider sx={{ mt: 1 }} />
        </Box>
      );
    
    case 'image':
      // For now, skip image fields in public inquiry form
      // (reference images are handled separately)
      return null;
    
    default:
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          required={field.required}
          placeholder={field.placeholder}
          helperText={field.helpText}
        />
      );
  }
};

const PublicInquiryForm = () => {
  const theme = useTheme();
  const { type, slug } = useParams(); // type = 'studio' or 'artist', slug = profile slug
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoReplySent, setAutoReplySent] = useState(false);
  const [error, setError] = useState('');
  
  // Profile data
  const [profile, setProfile] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formSettings, setFormSettings] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    project_description: '',
    budget_range: '',
    preferred_timeframe: '',
    is_first_tattoo: false,
    placement: '',
    size_estimate: '',
    additional_notes: '',
    referral_source: ''
  });
  
  // Custom form fields data
  const [customFieldData, setCustomFieldData] = useState({});
  
  // Reference images
  const [referenceImages, setReferenceImages] = useState([]);

  // Fetch profile and form template
  useEffect(() => {
    fetchFormData();
  }, [type, slug]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate params
      if (!type || !slug) {
        throw new Error('Invalid form URL. Please check the link and try again.');
      }
      
      if (!['studio', 'artist'].includes(type)) {
        throw new Error('Invalid form type. Must be studio or artist.');
      }
      // Fetch profile and inquiry form settings
      const response = await fetch(`${API_URL}/api/public/${type}/${slug}/inquiry-form/`);
      
      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('API returned non-JSON response:', contentType);
        throw new Error('Form not available. The service may be temporarily unavailable.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error(errorData.error || 'Inquiry form not found or not enabled');
        }
        throw new Error(errorData.error || 'Failed to load inquiry form');
      }
      
      const data = await response.json();
      setProfile(data.profile);
      setFormTemplate(data.form_template);
      setFormSettings(data.settings);
      
    } catch (err) {
      console.error('Error loading inquiry form:', err);
      setError(err.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.client_name.trim()) {
      errors.push('Name is required');
    }
    
    if (!formData.client_email.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!formData.project_description.trim()) {
      errors.push('Project description is required');
    }
    
    // Check form settings requirements
    if (formSettings?.require_phone && !formData.client_phone.trim()) {
      errors.push('Phone number is required');
    }
    
    if (formSettings?.require_budget && !formData.budget_range) {
      errors.push('Budget selection is required');
    }
    
    if (formSettings?.require_timeframe && !formData.preferred_timeframe) {
      errors.push('Timeframe selection is required');
    }
    
    // Validate custom form required fields
    if (formTemplate?.elements) {
      formTemplate.elements.forEach(field => {
        if (field.required && !customFieldData[field.id]) {
          errors.push(`${field.label} is required`);
        }
      });
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      // Prepare form data for submission
      const submitData = new FormData();
      submitData.append('inquiry_type', type);
      submitData.append('slug', slug);
      submitData.append('client_name', formData.client_name);
      submitData.append('client_email', formData.client_email);
      submitData.append('client_phone', formData.client_phone);
      submitData.append('project_description', formData.project_description);
      submitData.append('budget_range', formData.budget_range);
      submitData.append('preferred_timeframe', formData.preferred_timeframe);
      submitData.append('is_first_tattoo', formData.is_first_tattoo);
      submitData.append('placement', formData.placement);
      submitData.append('size_estimate', formData.size_estimate);
      submitData.append('additional_notes', formData.additional_notes);
      submitData.append('referral_source', formData.referral_source);
      
      // Add custom form data as JSON
      if (Object.keys(customFieldData).length > 0) {
        submitData.append('custom_form_data', JSON.stringify(customFieldData));
      }
      
      // Add form template ID if using one
      if (formTemplate?.id) {
        submitData.append('form_template_id', formTemplate.id);
      }
      
      // Add reference images - Use indexed field names
      referenceImages.forEach((img, index) => {
        if (img.file) {
          submitData.append(`reference_image_${index}`, img.file);
        }
      });
      submitData.append('image_count', referenceImages.length.toString());
      
      const response = await fetch(`${API_URL}/api/public/inquiry/submit/`, {
        method: 'POST',
        body: submitData
      });
      
      // Check for HTML response
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Failed to submit form. Please try again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit inquiry');
      }
      
      // Get response data to check if auto-reply was sent
      const responseData = await response.json();
      setAutoReplySent(responseData.auto_reply_sent || false);
      setSubmitted(true);
      
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setError(err.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading inquiry form...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error && !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Form Not Available
          </Typography>
          <Typography color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in={true}>
          <Paper 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
            }}
          >
            <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Request Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Thank you for your inquiry! {profile?.artist_name || profile?.shop_name || 'The artist'} will review your request and get back to you soon.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {autoReplySent ? (
                <>A confirmation email has been sent to <strong>{formData.client_email}</strong></>
              ) : (
                <>We'll contact you at <strong>{formData.client_email}</strong> once your request has been reviewed.</>
              )}
            </Typography>
          </Paper>
        </Fade>
      </Container>
    );
  }

  // Main form
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header with profile info */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 3, 
          textAlign: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
        }}
      >
        {profile?.logo_url && (
          <Box sx={{ mb: 2 }}>
            <img 
              src={profile.logo_url} 
              alt={profile.shop_name || profile.name}
              style={{ 
                maxHeight: 80, 
                maxWidth: 200,
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
        
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Request an Appointment
        </Typography>
        
        {profile?.shop_name && (
          <Typography variant="h6" color="text.secondary">
            {profile.shop_name}
          </Typography>
        )}
        
        {profile?.artist_name && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {profile.artist_name}
          </Typography>
        )}
        
        {profile?.tagline && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            {profile.tagline}
          </Typography>
        )}
      </Paper>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Inquiry form */}
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Contact Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                Your Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Your Name"
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                required
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.client_email}
                onChange={(e) => handleInputChange('client_email', e.target.value)}
                required
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Phone Number${formSettings?.require_phone ? ' *' : ' (Optional)'}`}
                value={formData.client_phone}
                onChange={(e) => handleInputChange('client_phone', e.target.value)}
                required={formSettings?.require_phone}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>How did you hear about me?</InputLabel>
                <Select
                  value={formData.referral_source}
                  onChange={(e) => handleInputChange('referral_source', e.target.value)}
                  label="How did you hear about me?"
                >
                  {REFERRAL_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_first_tattoo}
                    onChange={(e) => handleInputChange('is_first_tattoo', e.target.checked)}
                  />
                }
                label="This will be my first tattoo"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Project Details Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon color="primary" />
                Project Details
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Describe Your Tattoo Idea"
                multiline
                rows={4}
                value={formData.project_description}
                onChange={(e) => handleInputChange('project_description', e.target.value)}
                required
                placeholder="Tell us about your tattoo idea, including style, theme, colors, and any other details..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Placement on Body"
                value={formData.placement}
                onChange={(e) => handleInputChange('placement', e.target.value)}
                placeholder="e.g., forearm, shoulder, back"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Approximate Size"
                value={formData.size_estimate}
                onChange={(e) => handleInputChange('size_estimate', e.target.value)}
                placeholder="e.g., 4 inches, half sleeve"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required={formSettings?.require_budget}>
                <InputLabel>Budget Range{!formSettings?.require_budget && ' (Optional)'}</InputLabel>
                <Select
                  value={formData.budget_range}
                  onChange={(e) => handleInputChange('budget_range', e.target.value)}
                  label={`Budget Range${!formSettings?.require_budget ? ' (Optional)' : ''}`}
                >
                  {BUDGET_RANGES.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required={formSettings?.require_timeframe}>
                <InputLabel>Preferred Timeframe{!formSettings?.require_timeframe && ' (Optional)'}</InputLabel>
                <Select
                  value={formData.preferred_timeframe}
                  onChange={(e) => handleInputChange('preferred_timeframe', e.target.value)}
                  label={`Preferred Timeframe${!formSettings?.require_timeframe ? ' (Optional)' : ''}`}
                >
                  {TIMEFRAME_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes (Optional)"
                multiline
                rows={2}
                value={formData.additional_notes}
                onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                placeholder="Any other information you'd like to share..."
              />
            </Grid>

            {/* Reference Images Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <ImageUploader
                images={referenceImages}
                onImagesChange={setReferenceImages}
                maxImages={formSettings?.max_reference_images || 3}
              />
            </Grid>

            {/* Custom Form Fields (if a template is selected) */}
            {formTemplate?.elements && formTemplate.elements.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DescriptionIcon color="primary" />
                    Additional Questions
                  </Typography>
                </Grid>
                
                {formTemplate.elements.map((field) => (
                  <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.id}>
                    <CustomFieldRenderer
                      field={field}
                      value={customFieldData[field.id]}
                      onChange={handleCustomFieldChange}
                    />
                  </Grid>
                ))}
              </>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ py: 1.5 }}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Powered by InkedUp Manager
        </Typography>
      </Box>
    </Container>
  );
};

export default PublicInquiryForm;